import { useEffect, useSyncExternalStore } from "react";
import { useOrganizationImpersonationContext } from "../contexts/OrganizationImpersonationContext";
import { useProfileContext } from "../contexts/ProfileContext";
import { useRepositoriesContext } from "../contexts/RepositoriesContext";
import { OversizeSheetGuardHint } from "../core/models/entities/LogoOptions";
import { INACTIVE_OVERSIZE_SHEET_GUARD } from "../data/models/mappers/mapLogoOptions";
import { enviroment } from "../enviroment";
import {
  DEFAULT_OVERSIZE_SHEET_REQUEST_TTL_HOURS,
  isOversizeSheetGuardActive,
  OversizeLogoInfo,
  OversizeSheetContext,
  readRequested,
  RequestedMap,
  requestedScopeKey,
  writeRequested,
} from "../utils/functions/oversizeSheet";

// Oversize sheet guard (flag REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED): the
// ONE dialog state, shared by every cart handler mount and read by the
// single <OversizeSheetDialogHost/> in App.tsx (inside CartProvider). The
// add-to-cart guard, the reorder filter and the 409 catch paths open it
// with their context; the host renders the dialog and sends the request.
// Module state on purpose (like useCartPriceCheck): the handler hook is
// mounted by many components and the dialog must be one, app-wide.

export interface OversizeSheetDialogState {
  open: boolean;
  context: OversizeSheetContext;
  logos: OversizeLogoInfo[];
  /** logoId -> ISO UTC of the request sent (this session, from sessionStorage). */
  requested: RequestedMap;
  /** Hide "Request new sheet size" (API says requests are not configured). */
  hideRequest: boolean;
  /** Show "Back to cart" (place context opened from the order details page). */
  backToCart: boolean;
  /** Hours a sent request is remembered (the hint's requestTtlHours). */
  ttlHours: number;
}

export interface OpenOversizeSheetOptions {
  hideRequest?: boolean;
  backToCart?: boolean;
  ttlHours?: number;
}

const CLOSED: OversizeSheetDialogState = {
  open: false,
  context: "add",
  logos: [],
  requested: {},
  hideRequest: false,
  backToCart: false,
  ttlHours: DEFAULT_OVERSIZE_SHEET_REQUEST_TTL_HOURS,
};

let state: OversizeSheetDialogState = CLOSED;
// The hint's requestTtlHours as last seen by an active guard hook: the 409
// catch paths (order details, checkout) open the dialog without a hint of
// their own and inherit it. Default = the API's default window.
let rememberedTtlHours = DEFAULT_OVERSIZE_SHEET_REQUEST_TTL_HOURS;
// The viewer (user + org the session acts under) the "Request sent" memory
// is read and written for; set by useOversizeSheetViewer (the dialog host
// and every guard hook mount) before any dialog can open.
let viewerScope = requestedScopeKey(undefined, undefined);
const listeners = new Set<() => void>();

function set(next: OversizeSheetDialogState): void {
  state = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): OversizeSheetDialogState {
  return state;
}

/** Opens (or replaces) the dialog. Flag off = no-op, so no host ever renders. */
export function openOversizeSheetDialog(
  logos: OversizeLogoInfo[],
  context: OversizeSheetContext,
  options?: OpenOversizeSheetOptions
): void {
  if (!enviroment.oversizeSheetGuardEnabled) return;
  if (!logos || logos.length === 0) return;
  const ttlHours =
    options?.ttlHours && options.ttlHours > 0 ? options.ttlHours : rememberedTtlHours;
  set({
    open: true,
    context,
    logos,
    requested: readRequested(viewerScope, ttlHours),
    hideRequest: options?.hideRequest === true,
    backToCart: options?.backToCart === true,
    ttlHours,
  });
}

export function closeOversizeSheetDialog(): void {
  if (!state.open) return;
  set({ ...state, open: false });
}

/** Remembers one logo as requested for this viewer (state + sessionStorage). */
export function markSheetSizeRequested(logoId: number): void {
  const requested = writeRequested(viewerScope, logoId, state.ttlHours);
  set({ ...state, requested });
}

/** The API said requests are not configured: hide the button for this dialog. */
export function hideOversizeSheetRequest(): void {
  if (state.hideRequest) return;
  set({ ...state, hideRequest: true });
}

/** Remembers the hint's TTL for dialogs opened without a hint (409 catch paths). */
export function rememberOversizeSheetTtlHours(ttlHours: number): void {
  if (Number.isFinite(ttlHours) && ttlHours > 0) rememberedTtlHours = ttlHours;
}

/**
 * Remembers the viewer (user B2C id + the org the session acts under) the
 * "Request sent" memory belongs to. A change while the dialog is open (an
 * act-as switch) re-reads the memory for the new viewer.
 */
export function rememberOversizeSheetViewer(
  userKey: string | undefined | null,
  orgId: number | undefined | null
): void {
  const scope = requestedScopeKey(userKey, orgId);
  if (scope === viewerScope) return;
  viewerScope = scope;
  if (state.open) set({ ...state, requested: readRequested(viewerScope, state.ttlHours) });
}

export function resetOversizeSheetDialogForTests(): void {
  rememberedTtlHours = DEFAULT_OVERSIZE_SHEET_REQUEST_TTL_HOURS;
  viewerScope = requestedScopeKey(undefined, undefined);
  set(CLOSED);
}

/**
 * Binds the module's viewer scope to the session: the profile's B2C id and
 * the act-as org (else the profile's org). Mounted by the dialog host and
 * by every guard hook. Flag off: still only a module variable, no storage.
 */
export function useOversizeSheetViewer(): void {
  const { b2CId, organizationId: profileOrgId } = useProfileContext();
  const { organizationId: actAsOrgId } = useOrganizationImpersonationContext();
  useEffect(() => {
    rememberOversizeSheetViewer(b2CId, actAsOrgId ?? profileOrgId);
  }, [b2CId, actAsOrgId, profileOrgId]);
}

export function useOversizeSheetDialogState(): OversizeSheetDialogState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export interface OversizeSheetGuardView {
  /** FE flag + API hint enabled + the session's org chain (or act-as) listed. */
  active: boolean;
  maxMm: number;
  /** Options for openOversizeSheetDialog from this session's hint. */
  dialogOptions: OpenOversizeSheetOptions;
  hint: OversizeSheetGuardHint;
}

/**
 * The session's view of the guard, from the already prefetched
 * logos/options/all (0 new calls: the query is disabled while the flag is
 * off and served from cache otherwise). Every field is inert with the flag
 * off: active false, dialogOptions {}.
 */
export function useOversizeSheetGuard(): OversizeSheetGuardView {
  const flag = enviroment.oversizeSheetGuardEnabled;
  const { logoRepository } = useRepositoriesContext();
  const { role } = useProfileContext();
  const { organizationId: actAsOrgId } = useOrganizationImpersonationContext();
  useOversizeSheetViewer();
  const options = logoRepository.useOptions({ enabled: flag });
  const hint = options.data?.oversizeSheetGuard ?? INACTIVE_OVERSIZE_SHEET_GUARD;
  const active = flag && isOversizeSheetGuardActive(hint, role, actAsOrgId);
  const ttlHours = hint.requestTtlHours;
  useEffect(() => {
    if (active) rememberOversizeSheetTtlHours(ttlHours);
  }, [active, ttlHours]);
  return {
    active,
    maxMm: hint.maxMm,
    dialogOptions: active
      ? { hideRequest: !hint.requestEnabled, ttlHours: hint.requestTtlHours }
      : {},
    hint,
  };
}
